import React, { useState } from 'react';
import { NavLink, useLocation } from 'react-router';
import { useQuery } from '@tanstack/react-query';
import {
  Building2,
  Stethoscope,
  Settings,
  LayoutDashboard,
  ChevronsLeft,
  ChevronsRight,
  Users,
  Handshake,
  Shield,
  ClipboardCheck,
  ClipboardList,
  Flag,
  List,
  Grid3X3,
  MapPin,
  ShieldAlert,
  Wrench,
} from 'lucide-react';
import { clinicApi } from '@features/clinics/services/clinicApi';
import { taskRequestApi } from '@features/tasks/services/taskRequestApi';
import { dataQualityApi } from '@features/clinics/services/dataQualityApi';
import { useFeatureFlags, type FeatureFlags } from '../context/useFeatureFlags';
import { usePagePermissions } from '../context/usePagePermissions';
import { usePermissions, type ClinicMgmtPermission } from '../context/usePermissions';
import { useAuth } from '../context/useAuth';
import { getInitials } from '../utils/stringUtils';
import './Sidebar.css';

/* ─── Types ─────────────────────────────────────────────── */

interface NavItem {
  type: 'item';
  path: string;
  icon: React.ReactElement;
  label: string;
  disabled?: boolean;
  badge?: number;
  featureFlag?: keyof FeatureFlags;
  pageKey?: string;
  /** Single new-style verb permission required to see this item. */
  permission?: ClinicMgmtPermission;
  /** User must hold at least one of these permissions to see this item. */
  anyOf?: ClinicMgmtPermission[];
}

interface NavGroup {
  type: 'group';
  id: string;
  label: string;
  icon: React.ReactElement;
  children: NavItem[];
  featureFlag?: keyof FeatureFlags;
  pageKey?: string;
  /** Single new-style verb permission required to see this group. */
  permission?: ClinicMgmtPermission;
  /** User must hold at least one of these permissions to see this group. */
  anyOf?: ClinicMgmtPermission[];
}

type NavEntry = NavItem | NavGroup;

/* ─── Props ─────────────────────────────────────────────── */

interface SidebarProps {
  isOpen: boolean;
  onToggle: () => void;
}

export default function Sidebar({ isOpen, onToggle }: Readonly<SidebarProps>) {
  const location = useLocation();
  const flags = useFeatureFlags();
  const { isLoading: pagePermsLoading, pages } = usePagePermissions();
  const { hasPermission, hasAnyPermission, isSysAdmin } = usePermissions();
  const { user } = useAuth();
  const [hoveredItem, setHoveredItem] = useState<string | null>(null);

  // ─── Verification badge ────────────────────────────────
  const { data: queueCountData } = useQuery({
    queryKey: ['verification-queue-count'],
    queryFn: () => clinicApi.getVerificationQueueCount(),
    refetchInterval: 5 * 60 * 1000,
  });
  const verificationCount = queueCountData?.count ?? 0;

  // ─── Task request badge ──────────────────────────────
  const { data: taskCountsData } = useQuery({
    queryKey: ['task-request-counts'],
    queryFn: () => taskRequestApi.getTaskRequestCounts(),
    refetchInterval: 5 * 60 * 1000,
  });
  const taskPendingCount = (taskCountsData ?? []).reduce((sum, c) => {
    if (c.status === 'New' || c.status === 'InProgress') return sum + c.count;
    return sum;
  }, 0);

  // ─── Data quality badge ────────────────────────────────
  const { data: dqCountData } = useQuery({
    queryKey: ['data-quality-badge-count'],
    queryFn: () => dataQualityApi.getBadgeCount(),
    refetchInterval: 5 * 60 * 1000,
  });
  const dataQualityCriticalCount = dqCountData?.criticalCount ?? 0;

  // ─── Navigation entries (category-based) ───────────────
  const allNavEntries: NavEntry[] = [
    {
      type: 'group',
      id: 'overview',
      label: 'Overview',
      icon: <LayoutDashboard size={20} />,
      featureFlag: 'dashboard',
      children: [
        { type: 'item', path: '/dashboard', icon: <LayoutDashboard size={20} />, label: 'Dashboard', pageKey: 'dashboard', permission: 'UserCanListClinicProfiles' },
      ],
    },
    {
      type: 'group',
      id: 'clinics',
      label: 'Clinics',
      icon: <Building2 size={20} />,
      featureFlag: 'clinics',
      children: [
        { type: 'item', path: '/clinics', icon: <Building2 size={20} />, label: 'Clinic Profile', pageKey: 'clinics', permission: 'UserCanListClinicProfiles' },
        { type: 'item', path: '/verification', icon: <ClipboardCheck size={20} />, label: 'Verification', badge: verificationCount, pageKey: 'verification', permission: 'UserCanListClinicVerificationQueue' },
        { type: 'item', path: '/clinic-matrix', icon: <Grid3X3 size={20} />, label: 'Clinic Matrix', pageKey: 'clinic-matrix', permission: 'UserCanListClinicProfiles' },
      ],
    },
    {
      type: 'group',
      id: 'relationships',
      label: 'Relationships',
      icon: <Users size={20} />,
      featureFlag: 'coverage',
      pageKey: 'coverage',
      permission: 'UserCanListClinicRelationships',
      children: [
        { type: 'item', path: '/client-clinic-relationships', icon: <Users size={20} />, label: 'Rel. Listing', featureFlag: 'clinicClientAccounts', permission: 'UserCanListClinicRelationships' },
        { type: 'item', path: '/customer-locations', icon: <Handshake size={20} />, label: 'Management', featureFlag: 'customerLocationBrowser', permission: 'UserCanListClinicRelationships' },
        { type: 'item', path: '/job-sites', icon: <Flag size={20} />, label: 'JobSites', featureFlag: 'coverage', permission: 'UserCanCreateJobSite' },
        { type: 'item', path: '/client-coverage', icon: <MapPin size={20} />, label: 'Client Coverage', featureFlag: 'clientCoverage', permission: 'UserCanListClinicRelationships' },
      ],
    },
    {
      type: 'group',
      id: 'other',
      label: 'Other',
      icon: <ClipboardList size={20} />,
      children: [
        { type: 'item', path: '/tasks', icon: <ClipboardList size={20} />, label: 'Tasks', badge: taskPendingCount, featureFlag: 'tasks', pageKey: 'tasks', permission: 'UserCanListClinicTaskRequests' },
        { type: 'item', path: '/procedures', icon: <Stethoscope size={20} />, label: 'Procedures', featureFlag: 'procedures', pageKey: 'procedures', permission: 'UserCanListClinicProcedures' },
        { type: 'item', path: '/settings', icon: <Settings size={20} />, label: 'Settings', pageKey: 'settings', permission: 'UserCanViewClinicMgmtSettings' },
      ],
    },
    {
      type: 'group',
      id: 'admin',
      label: 'Admin',
      icon: <Shield size={20} />,
      featureFlag: 'admin',
      pageKey: 'admin',
      anyOf: [
        'UserCanRunClinicMatrixImport',
        'UserCanRunClinicGeocoding',
        'UserCanRunClinicDeactivation',
        'UserCanRunClinicAutoHierarchy',
        'UserCanRunLabCorpImport',
        'UserCanManageClinicProcedureTemplates',
        'UserCanManageClinicCapabilityTypes',
        'UserCanManageClinicCategoryTypes',
        'UserCanViewClinicDataQuality',
      ],
      children: [
        { type: 'item', path: '/data-quality', icon: <ShieldAlert size={20} />, label: 'Data Quality', badge: dataQualityCriticalCount, permission: 'UserCanViewClinicDataQuality' },
        { type: 'item', path: '/admin', icon: <Shield size={20} />, label: 'Admin Tools', anyOf: [
          'UserCanRunClinicMatrixImport',
          'UserCanRunClinicGeocoding',
          'UserCanRunClinicDeactivation',
          'UserCanRunClinicAutoHierarchy',
          'UserCanRunLabCorpImport',
          'UserCanManageClinicProcedureTemplates',
        ] },
        { type: 'item', path: '/admin/cutover', icon: <Wrench size={20} />, label: 'Cutover Tools', featureFlag: 'adminCutover', anyOf: [
          'UserCanRunClinicMatrixImport',
          'UserCanRunClinicGeocoding',
          'UserCanRunClinicDeactivation',
          'UserCanRunClinicAutoHierarchy',
          'UserCanRunLabCorpImport',
          'UserCanManageClinicProcedureTemplates',
        ] },
        { type: 'item', path: '/admin/lookups', icon: <List size={20} />, label: 'Lookup Tables', anyOf: [
          'UserCanManageClinicCapabilityTypes',
          'UserCanManageClinicCategoryTypes',
        ] },
      ],
    },
  ];

  // Returns true if the entry passes the access check.
  // Logic per axis:
  //   - featureFlag: hard block when disabled (404-equivalent)
  //   - pageKey + permission/anyOf: OR-logic — pass if ANY one allows.
  //     SysAdmin always passes.
  //
  // The legacy pageKey check uses an *explicit* match against the loaded pages map
  // (not hasPageAccess(), which returns true while data is still loading and for
  // unknown keys). Without this, the page-permission system's "fail open during
  // load" behavior would briefly bypass the new verb-permission check on first
  // render. Verb permissions are the source of truth post-cutover.
  const passesAccess = (entry: { featureFlag?: keyof FeatureFlags; pageKey?: string; permission?: ClinicMgmtPermission; anyOf?: ClinicMgmtPermission[] }) => {
    if (entry.featureFlag && !flags[entry.featureFlag]) return false;

    const hasAnyAccessRule = !!(entry.pageKey || entry.permission || entry.anyOf?.length);
    if (!hasAnyAccessRule) return true; // no rules → allow

    if (isSysAdmin) return true;

    const pageKeyExplicitlyAllowed =
      !!entry.pageKey && !pagePermsLoading && pages[entry.pageKey] === true;
    const permissionAllowed =
      (entry.permission ? hasPermission(entry.permission) : false)
      || (entry.anyOf?.length ? hasAnyPermission(...entry.anyOf) : false);

    return pageKeyExplicitlyAllowed || permissionAllowed;
  };

  const navEntries = allNavEntries
    .filter(passesAccess)
    .map((entry) => {
      if (entry.type === 'group') {
        return {
          ...entry,
          children: entry.children.filter(passesAccess),
        };
      }
      return entry;
    })
    .filter((entry) => {
      if (entry.type === 'group' && entry.children.length === 0) return false;
      return true;
    });

  // ─── Helpers ───────────────────────────────────────────

  // Flat list of all visible nav-item paths, used for longest-prefix active
  // matching so a parent route (e.g. /admin) doesn't stay highlighted alongside
  // a child route that is its own nav item (e.g. /admin/cutover, /admin/lookups).
  const allItemPaths = navEntries.flatMap((e) =>
    e.type === 'group' ? e.children.map((c) => c.path) : [e.path]
  );

  const isActiveRoute = (path: string) => {
    const current = location.pathname;
    if (current === path) return true;
    // A child URL (e.g. /clinics/123) keeps its item active — but only if no
    // more-specific sibling nav item is itself a match (e.g. /admin vs /admin/cutover).
    if (current.startsWith(`${path}/`)) {
      const moreSpecificMatch = allItemPaths.some(
        (other) =>
          other !== path &&
          other.startsWith(`${path}/`) &&
          (current === other || current.startsWith(`${other}/`))
      );
      return !moreSpecificMatch;
    }
    return false;
  };

  // ─── Render helpers ────────────────────────────────────

  const renderNavItem = (item: NavItem) => (
    <li
      key={item.path}
      className="nav-item"
      onMouseEnter={() => setHoveredItem(item.path)}
      onMouseLeave={() => setHoveredItem(null)}
    >
      {item.disabled ? (
        <span className="nav-link disabled">
          <span className="nav-icon">{item.icon}</span>
          <span className={`nav-label ${isOpen ? 'visible' : 'hidden'}`}>
            {item.label}
          </span>
          {!isOpen && hoveredItem === item.path && (
            <div className="nav-tooltip">{item.label} (Coming Soon)</div>
          )}
        </span>
      ) : (
        <NavLink
          to={item.path}
          className={() =>
            `nav-link ${isActiveRoute(item.path) ? 'active' : ''}`
          }
        >
          <span className="nav-icon">
            {item.icon}
            {item.badge !== undefined && item.badge > 0 && !isOpen && (
              <span className="nav-badge-dot" />
            )}
          </span>
          <span className={`nav-label ${isOpen ? 'visible' : 'hidden'}`}>
            {item.label}
            {item.badge !== undefined && item.badge > 0 && (
              <span className="nav-badge">{item.badge > 99 ? '99+' : item.badge}</span>
            )}
          </span>
          {!isOpen && hoveredItem === item.path && (
            <div className="nav-tooltip">
              {item.label}
              {item.badge !== undefined && item.badge > 0 && ` (${item.badge})`}
            </div>
          )}
        </NavLink>
      )}
    </li>
  );

  const renderNavGroup = (group: NavGroup) => {
    // Collapsed sidebar: render children as flat items (no category header)
    if (!isOpen) {
      return (
        <React.Fragment key={group.id}>
          {group.children.map((child) => renderNavItem(child))}
        </React.Fragment>
      );
    }

    // Expanded sidebar: category label + children (always visible)
    return (
      <li key={group.id} className="nav-category">
        <div className="nav-category-label">{group.label}</div>
        <ul className="nav-category-items">
          {group.children.map((child) => renderNavItem(child))}
        </ul>
      </li>
    );
  };

  const displayName = user?.displayName || user?.email || 'User';
  const initials = getInitials(displayName);
  const primaryRole = user?.roles?.[0] || '';

  // ─── Main render ───────────────────────────────────────

  return (
    <aside className={`sidebar ${isOpen ? 'expanded' : 'collapsed'}`}>
      <div className="sidebar-top">
        {user && (
          <NavLink
            to="/profile"
            className={({ isActive }) => `sidebar-user ${isActive ? 'active' : ''}`}
            title={isOpen ? undefined : `${displayName} — View profile`}
          >
            <div className="sidebar-user-avatar">{initials}</div>
            <div className={`sidebar-user-info ${isOpen ? 'visible' : 'hidden'}`}>
              <div className="sidebar-user-name">{displayName}</div>
              {primaryRole && <div className="sidebar-user-role">{primaryRole}</div>}
            </div>
          </NavLink>
        )}
        <button
          type="button"
          className="sidebar-toggle"
          onClick={onToggle}
          aria-label={isOpen ? 'Collapse sidebar' : 'Expand sidebar'}
          title={isOpen ? 'Collapse sidebar' : 'Expand sidebar'}
        >
          {isOpen ? (
            <ChevronsLeft size={18} strokeWidth={2.5} />
          ) : (
            <ChevronsRight size={18} strokeWidth={2.5} />
          )}
        </button>
      </div>

      <nav className="sidebar-nav">
        <ul className="nav-list">
          {navEntries.map((entry) => {
            if (entry.type === 'item') return renderNavItem(entry);
            if (entry.type === 'group') return renderNavGroup(entry);
            return null;
          })}
        </ul>
      </nav>
    </aside>
  );
}
